import { useState, useEffect, useRef } from "react";
import { C, S, fontCond } from "../constants";
import { daysUntilDeadline } from "../lib/helpers";
import { getBlockStatus, getChallengeStatus, getPlayerStats } from "../lib/helpers";

// ─── AVATAR ───────────────────────────────────────────────────────────────────

function mkAvatar(name) {
  const parts = name.trim().split(" ");
  const initStr = ((parts[0]?.[0] || "") + (parts[1]?.[0] || "")).toUpperCase();
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 40 40"><rect width="40" height="40" fill="#dde3ea"/><text x="20" y="26" text-anchor="middle" font-family="Arial,sans-serif" font-size="15" font-weight="700" fill="#005095">${initStr}</text></svg>`;
  return "data:image/svg+xml;base64," + btoa(unescape(encodeURIComponent(svg)));
}

export const Avatar = ({ player, size = 40 }) => {
  const src = player.photo || mkAvatar(player.name);
  return (
    <img
      src={src}
      alt={player.name}
      style={{
        width: size, height: size, borderRadius: "50%",
        objectFit: "cover", display: "block", flexShrink: 0,
        border: "1px solid #e0e4eb",
      }}
    />
  );
};

// ─── PLAYER POPOVER ───────────────────────────────────────────────────────────
// Wrap qualquer elemento clicável para exibir mini-perfil com contatos.
// Uso: <PlayerPopover player={p} isLoggedIn={!!currentUser}><Avatar .../></PlayerPopover>

export const PlayerPopover = ({ player, isLoggedIn, children }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    function handleClick(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", handleClick);
    document.addEventListener("touchstart", handleClick);
    return () => {
      document.removeEventListener("mousedown", handleClick);
      document.removeEventListener("touchstart", handleClick);
    };
  }, [open]);

  if (!isLoggedIn) return <>{children}</>;

  const hasPhone = player.phone && player.phone.trim();
  const hasEmail = player.email && player.email.trim();
  const hasContact = hasPhone || hasEmail;

  function waLink(phone) {
    const digits = phone.replace(/\D/g, "");
    const num = digits.startsWith("55") ? digits : "55" + digits;
    return "https://wa.me/" + num;
  }

  return (
    <div ref={ref} style={{ position: "relative", display: "inline-flex" }}>
      <div
        onClick={e => { e.stopPropagation(); setOpen(v => !v); }}
        style={{ cursor: "pointer" }}
      >
        {children}
      </div>

      {open && (
        <div style={{
          position: "absolute",
          bottom: "calc(100% + 8px)",
          left: "50%",
          transform: "translateX(-50%)",
          background: C.card,
          border: "1px solid " + C.border,
          borderRadius: 12,
          boxShadow: "0 8px 28px rgba(0,0,0,.18)",
          zIndex: 1000,
          minWidth: 200,
          maxWidth: 260,
          padding: "14px 16px",
        }}>
          {/* Seta */}
          <div style={{
            position: "absolute", bottom: -7, left: "50%", transform: "translateX(-50%)",
            width: 14, height: 7, overflow: "hidden",
          }}>
            <div style={{
              width: 10, height: 10, background: C.card,
              border: "1px solid " + C.border,
              transform: "rotate(45deg)", marginLeft: 2, marginTop: -5,
              boxShadow: "1px 1px 3px rgba(0,0,0,.08)",
            }}/>
          </div>

          {/* Foto + nome + posição */}
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
            <Avatar player={player} size={44}/>
            <div>
              <div style={{ fontWeight: 700, fontSize: 14, color: C.text, lineHeight: 1.2 }}>{player.name}</div>
              <div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>#{player.position}</div>
            </div>
          </div>

          {/* Contatos */}
          {hasContact ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {hasPhone && (
                <a
                  href={waLink(player.phone)}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={e => e.stopPropagation()}
                  style={{
                    display: "flex", alignItems: "center", gap: 8,
                    background: "#e9f7ef", border: "1px solid #b7dfc7",
                    borderRadius: 8, padding: "8px 11px",
                    textDecoration: "none", color: "#1a7a46", fontSize: 13, fontWeight: 600,
                  }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="#25D366" style={{ flexShrink: 0 }}>
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                  </svg>
                  {player.phone}
                </a>
              )}
              {hasEmail && (
                <a
                  href={"mailto:" + player.email}
                  onClick={e => e.stopPropagation()}
                  style={{
                    display: "flex", alignItems: "center", gap: 8,
                    background: "#eef2ff", border: "1px solid #c3cdf7",
                    borderRadius: 8, padding: "8px 11px",
                    textDecoration: "none", color: "#3451b2", fontSize: 13, fontWeight: 600,
                    wordBreak: "break-all",
                  }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#3451b2" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                    <rect x="2" y="4" width="20" height="16" rx="2"/>
                    <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/>
                  </svg>
                  {player.email}
                </a>
              )}
            </div>
          ) : (
            <div style={{ fontSize: 12, color: C.muted, fontStyle: "italic" }}>Sem contatos cadastrados.</div>
          )}
        </div>
      )}
    </div>
  );
};

// ─── STAT CARD ────────────────────────────────────────────────────────────────

export const StatCard = ({ label, value, sub, color = C.primary }) => (
  <div style={{
    background: C.cardAlt, borderRadius: 12, padding: "14px 18px",
    flex: 1, minWidth: 80, border: "1px solid " + C.border,
  }}>
    <div style={{
      fontSize: 10, fontWeight: 700, letterSpacing: 2,
      color: C.muted, textTransform: "uppercase", marginBottom: 5,
    }}>{label}</div>
    <div style={{
      fontSize: 26, fontWeight: 900, color, lineHeight: 1, fontFamily: fontCond,
    }}>{value}</div>
    {sub && <div style={{ fontSize: 11, color: C.muted, marginTop: 3 }}>{sub}</div>}
  </div>
);

// ─── WIN RATE BAR ─────────────────────────────────────────────────────────────

export const WinRateBar = ({ rate }) => (
  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
    <div style={{ flex: 1, height: 5, background: C.border, borderRadius: 3, overflow: "hidden" }}>
      <div style={{
        width: rate + "%", height: "100%",
        background: rate >= 60 ? C.success : rate >= 40 ? C.warning : C.primary,
        borderRadius: 3, transition: "width 0.6s ease",
      }} />
    </div>
    <span style={{ fontSize: 12, fontWeight: 700, color: C.muted, minWidth: 30 }}>{rate}%</span>
  </div>
);

// ─── DEADLINE CHIP ────────────────────────────────────────────────────────────

export const DeadlineChip = ({ proposedAt }) => {
  const days = daysUntilDeadline(proposedAt);
  if (days === null) return null;
  const color = days <= 1 ? C.primary : days <= 3 ? C.warning : C.muted;
  const label = days <= 0 ? "Expirado" : days === 1 ? "Expira amanhã" : `${days}d restantes`;
  return (
    <span style={{
      display: "inline-flex", alignItems: "center",
      background: color + "22", color,
      border: "1px solid " + color + "44",
      borderRadius: 20, padding: "2px 9px",
      fontSize: 11, fontWeight: 700, lineHeight: 1,
    }}>{label}</span>
  );
};

// ─── YELLOW TRIANGLE ─────────────────────────────────────────────────────────

export const YellowTriangle = ({ size = 14 }) => (
  <svg
    width={size} height={size} viewBox="0 0 10 10"
    style={{ display: "inline-block", verticalAlign: "middle", flexShrink: 0 }}
  >
    <polygon points="5,0.5 9.5,9.5 0.5,9.5" fill="#FFD700" stroke="#e6b800" strokeWidth="0.4" />
  </svg>
);

// ─── PLAYER CARD (mobile list row) ───────────────────────────────────────────

export const PlayerListRow = ({ p, me, state, onPress, isLoggedIn }) => {
  const isMe = me && p.id === me.id;
  const bl = getBlockStatus(p);
  const cs = getChallengeStatus(p, state.challenges);
  const st = getPlayerStats(p.name, state.results);

  return (
    <div
      onClick={onPress}
      style={{
        display: "flex", alignItems: "center", gap: 12,
        padding: "14px 0", borderBottom: "1px solid " + C.border,
        cursor: "pointer",
        background: isMe ? "#fff0f2" : "transparent",
        WebkitTapHighlightColor: "transparent",
      }}
    >
      {/* Posição + delta */}
      <div style={{
        display: "flex", flexDirection: "column", alignItems: "center",
        width: 32, flexShrink: 0, gap: 1,
      }}>
        <div style={{
          fontFamily: fontCond, fontWeight: 900, fontSize: 15,
          color: p.position <= 3 ? C.warning : C.muted, lineHeight: 1,
        }}>
          {p.position === 1 ? "🥇" : p.position === 2 ? "🥈" : p.position === 3 ? "🥉" : p.position}
        </div>
        {p.positionDelta > 0 && (
          <div style={{ fontSize: 10, fontWeight: 800, color: C.success }}>▲{p.positionDelta}</div>
        )}
        {p.positionDelta < 0 && (
          <div style={{ fontSize: 10, fontWeight: 800, color: "#e53e3e" }}>▼{Math.abs(p.positionDelta)}</div>
        )}
      </div>

      {/* Avatar com popover */}
      <PlayerPopover player={p} isLoggedIn={isLoggedIn}>
        <Avatar player={p} size={40} />
      </PlayerPopover>

      {/* Info */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontWeight: 600, fontSize: 14,
          color: isMe ? C.primary : C.text,
          whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
        }}>
          {p.name}{isMe ? " (você)" : ""}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 3, flexWrap: "wrap" }}>
          {bl === "inativo"
            ? <span style={{ ...S.badge("#666666"), fontSize: 10, padding: "1px 7px" }}>Inativo</span>
            : (cs === "desafiado" || cs === "ambos" || bl === "bloqueado")
            ? <span style={{ ...S.badge("#e53e3e"), fontSize: 10, padding: "1px 7px" }}>Indisponível</span>
            : <span style={{ ...S.badge(C.accent), fontSize: 10, padding: "1px 7px" }}>Disponível</span>
          }
          {st.total > 0 && (
            <span style={{ fontSize: 11, color: C.muted }}>
              {st.wins + st.woWins}V {st.losses + st.woLosses}D
            </span>
          )}
        </div>
      </div>

      {/* Win rate */}
      {st.total > 0 && (
        <div style={{ textAlign: "right", flexShrink: 0, minWidth: 48 }}>
          <div style={{
            fontFamily: fontCond, fontWeight: 700, fontSize: 15,
            color: st.winRate >= 60 ? C.success : C.muted,
          }}>{st.winRate}%</div>
          <div style={{ fontSize: 10, color: C.muted }}>{st.total} jogos</div>
        </div>
      )}

      <div style={{ color: C.border, fontSize: 16 }}>›</div>
    </div>
  );
};

// ─── CLUB ICON (mobile — circle emblem only) ──────────────────────────────────

export const ClubIcon = ({ size = 32 }) => (
  <svg
    width={size} height={size} viewBox="0 0 16.6 16.6"
    xmlns="http://www.w3.org/2000/svg"
    style={{ display: "block", flexShrink: 0 }}
  >
    <path d="M16.6,8.3c0,4.58-3.72,8.3-8.3,8.3S0,12.88,0,8.3,3.72,0,8.3,0s8.3,3.72,8.3,8.3" fill="#005095"/>
    <path d="M8.3,14.11c-3.21,0-5.81-2.6-5.81-5.81S5.09,2.49,8.3,2.49c1.5,0,2.87,.57,3.9,1.5l.88-.88c-1.26-1.16-2.94-1.87-4.78-1.87C4.4,1.24,1.25,4.4,1.25,8.3s3.16,7.06,7.06,7.06c1.84,0,3.52-.71,4.78-1.87l-.88-.88c-1.03,.94-2.4,1.5-3.9,1.5" fill="#ffdc00"/>
    <path d="M8.3,11.62c-1.83,0-3.32-1.49-3.32-3.32s1.49-3.32,3.32-3.32c.81,0,1.56,.29,2.13,.78l.88-.88c-.8-.71-1.86-1.14-3.02-1.14-2.52,0-4.57,2.04-4.57,4.57s2.04,4.57,4.57,4.57c1.16,0,2.21-.43,3.02-1.14l-.88-.88c-.58,.48-1.32,.78-2.13,.78" fill="#ffdc00"/>
    <path d="M13.09,8.68c-.27,0-.51-.09-.71-.24-.04-.03-.08-.06-.11-.1-.07-.08-.16-.13-.27-.13-.04,0-.07,0-.1,.02-.07,0-.14,.02-.22,.02-.23,0-.45-.05-.65-.13-.3-2.68-1.85-4.98-4.05-6.32,1.46,1.53,2.35,3.6,2.35,5.87,0,.11,0,.23,0,.34,0,0-1.63,.43-2.17,.6-.09,.03-.26,.09-.35,.13-.47,.2-.85,.58-1.07,1.05,.1,.19,.22,.36,.35,.52h0c.03-.57,.48-1.03,1.05-1.08,.2-.02,.39,.02,.56,.09,.52,.24,1.11,.37,1.74,.37,.67,0,1.33-.14,1.89-.43,.54-.29,1.14-.48,1.77-.54v-.03h0" fill="#fff"/>
  </svg>
);

// ─── CLUB LOGO (desktop — full logo with text) ────────────────────────────────

export const ClubLogo = ({ height = 32 }) => {
  const w = Math.round(height * (141.73 / 16.6));
  return (
    <svg
      width={w} height={height} viewBox="0 0 141.73 16.6"
      xmlns="http://www.w3.org/2000/svg"
      style={{ display: "block", flexShrink: 0 }}
    >
      <g>
        <path d="M16.6,8.3c0,4.58-3.72,8.3-8.3,8.3S0,12.88,0,8.3,3.72,0,8.3,0s8.3,3.72,8.3,8.3" fill="#005095"/>
        <path d="M8.3,14.11c-3.21,0-5.81-2.6-5.81-5.81S5.09,2.49,8.3,2.49c1.5,0,2.87,.57,3.9,1.5l.88-.88c-1.26-1.16-2.94-1.87-4.78-1.87C4.4,1.24,1.25,4.4,1.25,8.3s3.16,7.06,7.06,7.06c1.84,0,3.52-.71,4.78-1.87l-.88-.88c-1.03,.94-2.4,1.5-3.9,1.5" fill="#ffdc00"/>
        <path d="M8.3,11.62c-1.83,0-3.32-1.49-3.32-3.32s1.49-3.32,3.32-3.32c.81,0,1.56,.29,2.13,.78l.88-.88c-.8-.71-1.86-1.14-3.02-1.14-2.52,0-4.57,2.04-4.57,4.57s2.04,4.57,4.57,4.57c1.16,0,2.21-.43,3.02-1.14l-.88-.88c-.58,.48-1.32,.78-2.13,.78" fill="#ffdc00"/>
        <path d="M13.09,8.68c-.27,0-.51-.09-.71-.24-.04-.03-.08-.06-.11-.1-.07-.08-.16-.13-.27-.13-.04,0-.07,0-.1,.02-.07,0-.14,.02-.22,.02-.23,0-.45-.05-.65-.13-.3-2.68-1.85-4.98-4.05-6.32,1.46,1.53,2.35,3.6,2.35,5.87,0,.11,0,.23,0,.34,0,0-1.63,.43-2.17,.6-.09,.03-.26,.09-.35,.13-.47,.2-.85,.58-1.07,1.05,.1,.19,.22,.36,.35,.52h0c.03-.57,.48-1.03,1.05-1.08,.2-.02,.39,.02,.56,.09,.52,.24,1.11,.37,1.74,.37,.67,0,1.33-.14,1.89-.43,.54-.29,1.14-.48,1.77-.54v-.03h0" fill="#fff"/>
      </g>
      <path d="M141.45,5.05c-.35-.24-.77-.45-1.24-.63-.47-.18-.99-.27-1.56-.27-.41,0-.79,.05-1.14,.16-.35,.11-.66,.27-.93,.48-.27,.21-.48,.48-.63,.79-.15,.32-.23,.68-.23,1.08,0,.31,.05,.58,.14,.8,.09,.23,.23,.42,.42,.59,.19,.17,.42,.31,.71,.43,.28,.12,.61,.24,1,.34,.32,.08,.6,.16,.86,.24s.48,.16,.66,.25c.18,.09,.32,.2,.42,.32,.1,.12,.15,.27,.15,.45,0,.55-.43,.83-1.29,.83-.31,0-.61-.04-.9-.11-.29-.07-.56-.17-.8-.28-.24-.11-.45-.22-.63-.33-.18-.11-.3-.2-.37-.27l-.69,1.34c.47,.33,.99,.59,1.58,.77,.59,.18,1.18,.27,1.78,.27,.39,0,.77-.04,1.13-.13,.36-.09,.68-.22,.95-.41,.27-.19,.49-.43,.65-.74,.16-.31,.24-.67,.24-1.1,0-.37-.06-.68-.18-.93-.12-.25-.29-.47-.51-.65-.22-.18-.49-.33-.81-.45-.32-.12-.67-.23-1.07-.34-.3-.08-.57-.15-.8-.21-.23-.07-.42-.14-.57-.23-.15-.08-.27-.18-.35-.29-.08-.11-.12-.25-.12-.41,0-.29,.1-.51,.32-.67,.21-.16,.53-.24,.96-.24,.24,0,.48,.03,.71,.09,.23,.06,.45,.13,.65,.22,.2,.09,.37,.18,.51,.27,.14,.09,.23,.16,.29,.21l.69-1.27Zm-8.91,4.09h-2.27l1.15-3.24,1.12,3.24Zm-3.26,3.09l.73-2h2.81l.73,2h1.65l-3.15-8.02h-1.26l-3.16,8.02h1.65Zm-6.84-6.65h1.89c.14,0,.27,.03,.4,.1,.13,.06,.25,.15,.35,.27,.1,.12,.18,.25,.24,.41,.06,.16,.09,.33,.09,.52s-.03,.35-.08,.51c-.05,.16-.12,.3-.21,.41-.09,.12-.2,.21-.32,.28-.12,.07-.26,.1-.4,.1h-1.95v-2.6Zm0,6.65v-2.69h1.54l1.68,2.69h1.76l-1.89-3.01c.45-.19,.8-.5,1.06-.92,.26-.42,.39-.9,.39-1.42,0-.32-.06-.64-.19-.96s-.3-.61-.53-.86c-.23-.25-.49-.45-.81-.61-.31-.15-.65-.23-1.02-.23h-3.55V12.23h1.56Zm-5.08-3.09h-2.27l1.15-3.24,1.12,3.24Zm-3.26,3.09l.73-2h2.81l.73,2h1.65l-3.15-8.02h-1.26l-3.16,8.02h1.65Zm-6.29,1.99c.15,.06,.33,.11,.53,.16,.2,.05,.43,.07,.68,.07,.49,0,.85-.09,1.09-.28,.24-.19,.36-.43,.36-.71s-.09-.48-.28-.6c-.19-.12-.41-.19-.68-.19-.06,0-.12,0-.19,.01-.06,0-.12,.02-.17,.03l.27-.43c.29-.03,.57-.09,.86-.19s.55-.22,.8-.38c.25-.16,.47-.34,.67-.55,.2-.21,.35-.45,.46-.72l-1.28-.73c-.09,.22-.21,.4-.35,.56-.14,.15-.3,.28-.47,.38-.17,.1-.34,.17-.52,.21-.18,.04-.36,.07-.53,.07-.33,0-.64-.08-.92-.23-.28-.15-.52-.35-.72-.6-.2-.25-.35-.54-.46-.86-.11-.33-.16-.67-.16-1.02,0-.32,.05-.63,.14-.96,.09-.32,.23-.6,.42-.86,.18-.25,.42-.46,.7-.61,.28-.16,.6-.24,.97-.24,.17,0,.34,.02,.52,.06,.18,.04,.36,.11,.53,.2,.17,.09,.33,.21,.47,.37,.14,.15,.26,.35,.35,.57l1.2-.82c-.25-.52-.63-.94-1.16-1.26-.52-.32-1.14-.49-1.86-.49-.61,0-1.15,.12-1.63,.35-.47,.23-.88,.54-1.2,.92-.33,.38-.58,.8-.75,1.28-.17,.48-.26,.96-.26,1.45s.09,.97,.26,1.45c.17,.48,.41,.91,.72,1.29,.31,.38,.68,.7,1.12,.95,.44,.25,.92,.39,1.43,.43l-.5,.98c.09-.02,.22-.05,.4-.08s.34-.05,.5-.05c.17,0,.31,.03,.42,.08,.11,.06,.16,.15,.16,.29s-.06,.23-.17,.29c-.11,.06-.27,.08-.46,.08s-.4-.02-.59-.07c-.19-.05-.33-.1-.42-.15l-.3,.55Zm-3.79-9.99h-1.56V12.23h1.56V4.22Zm-5.08,4.91h-2.27l1.15-3.24,1.12,3.24Zm-3.26,3.09l.73-2h2.81l.73,2h1.65l-3.15-8.02h-1.27l-3.16,8.02h1.65Zm-8.49-2.56c.18,.49,.45,.93,.78,1.32,.33,.39,.73,.7,1.2,.94,.46,.24,.97,.36,1.53,.36,.32,0,.64-.04,.96-.12,.32-.08,.63-.2,.93-.36,.29-.16,.55-.35,.78-.58,.23-.23,.41-.49,.53-.8l-1.28-.73c-.09,.22-.21,.4-.35,.56-.14,.15-.3,.28-.47,.38-.17,.1-.35,.17-.53,.21-.18,.04-.36,.07-.53,.07-.33,0-.64-.08-.91-.23-.28-.15-.52-.35-.72-.6-.2-.25-.35-.54-.46-.86-.11-.33-.16-.67-.16-1.02,0-.32,.05-.63,.14-.96,.09-.32,.23-.6,.42-.86,.18-.25,.42-.46,.69-.61,.28-.16,.6-.24,.97-.24,.17,0,.34,.02,.52,.06,.18,.04,.36,.11,.53,.2,.17,.09,.32,.21,.47,.37,.14,.15,.26,.35,.35,.57l1.2-.82c-.25-.52-.63-.94-1.16-1.26-.52-.32-1.14-.49-1.86-.49-.61,0-1.15,.12-1.63,.35-.47,.23-.88,.54-1.2,.92-.33,.38-.58,.8-.75,1.28-.17,.48-.26,.96-.26,1.45,0,.51,.09,1.02,.28,1.51m-3.97-4.62c-.35-.24-.77-.45-1.24-.63s-.99-.27-1.56-.27c-.41,0-.79,.05-1.14,.16-.35,.11-.66,.27-.93,.48-.27,.21-.48,.48-.63,.79-.15,.32-.23,.68-.23,1.08,0,.31,.05,.58,.14,.8,.09,.23,.23,.42,.42,.59,.19,.17,.42,.31,.71,.43,.28,.12,.62,.24,1,.34,.32,.08,.6,.16,.86,.24s.47,.16,.66,.25c.18,.09,.32,.2,.42,.32,.1,.12,.15,.27,.15,.45,0,.55-.43,.83-1.29,.83-.31,0-.61-.04-.9-.11-.29-.07-.56-.17-.8-.28-.24-.11-.45-.22-.63-.33-.18-.11-.3-.2-.37-.27l-.69,1.34c.47,.33,.99,.59,1.58,.77,.59,.18,1.18,.27,1.78,.27,.39,0,.77-.04,1.13-.13,.36-.09,.68-.22,.95-.41,.27-.19,.49-.43,.65-.74,.16-.31,.24-.67,.24-1.1,0-.37-.06-.68-.18-.93-.12-.25-.29-.47-.51-.65-.22-.18-.49-.33-.81-.45-.32-.12-.68-.23-1.07-.34-.3-.08-.57-.15-.8-.21-.23-.07-.42-.14-.57-.23-.15-.08-.27-.18-.35-.29s-.12-.25-.12-.41c0-.29,.1-.51,.32-.67,.21-.16,.53-.24,.96-.24,.24,0,.48,.03,.71,.09,.23,.06,.45,.13,.65,.22,.2,.09,.37,.18,.51,.27,.14,.09,.23,.16,.29,.21l.69-1.27Zm-12.53,2.2c.1-.32,.24-.61,.43-.86,.19-.25,.42-.46,.7-.61,.27-.15,.59-.23,.95-.23s.66,.07,.93,.22c.27,.15,.51,.34,.7,.59,.19,.25,.34,.53,.45,.86,.11,.32,.16,.66,.16,1.01s-.05,.66-.15,.98c-.1,.32-.24,.61-.43,.86s-.42,.46-.7,.61c-.27,.15-.59,.23-.95,.23s-.67-.08-.95-.23c-.28-.15-.51-.35-.71-.6-.19-.25-.34-.53-.44-.86-.1-.32-.15-.66-.15-1.01s.05-.66,.15-.98m3.6,4.71c.47-.22,.87-.52,1.21-.89,.34-.37,.61-.8,.8-1.29,.19-.49,.29-1,.29-1.53,0-.51-.09-1-.27-1.49-.18-.49-.44-.93-.77-1.31-.33-.38-.73-.69-1.2-.93-.47-.24-.98-.36-1.55-.36s-1.06,.11-1.52,.33c-.47,.22-.87,.52-1.21,.9-.34,.38-.61,.81-.8,1.3-.19,.49-.29,1-.29,1.54s.09,1.01,.27,1.5c.18,.49,.44,.92,.77,1.3,.34,.38,.74,.68,1.2,.92,.47,.23,.98,.34,1.55,.34s1.06-.11,1.52-.33m-7.82-2.69c-.11,.32-.26,.6-.46,.84-.2,.24-.45,.42-.74,.56-.3,.14-.64,.2-1.02,.2h-1.35V5.58h1.35c.38,0,.71,.06,1.01,.19,.3,.13,.55,.31,.75,.54,.2,.23,.36,.51,.46,.83,.1,.32,.16,.68,.16,1.06s-.05,.73-.16,1.05m-2.23,2.97c.6,0,1.15-.1,1.63-.29,.48-.19,.9-.46,1.25-.81,.35-.35,.61-.77,.8-1.26,.18-.49,.28-1.05,.28-1.65,0-.55-.08-1.07-.25-1.55-.17-.49-.41-.91-.75-1.27-.33-.36-.74-.65-1.24-.86-.49-.21-1.07-.32-1.72-.32h-2.91V12.23h2.91Zm-10.87-1.37v-2.09h3.38v-1.26h-3.38v-1.93h3.91v-1.37h-5.47V12.23h5.57v-1.37h-4.01Zm-5.53-5.32c.25,0,.46,.09,.63,.27,.17,.18,.25,.42,.25,.73s-.09,.56-.28,.74c-.18,.18-.41,.27-.67,.27h-2.08v-2.02h2.15Zm1.11,4.73c-.05,.13-.12,.24-.21,.33-.09,.09-.2,.17-.32,.22-.12,.05-.26,.08-.4,.08h-2.32v-2.11h2.39c.14,0,.26,.03,.37,.09,.11,.06,.21,.13,.29,.23s.15,.21,.2,.34c.05,.13,.07,.27,.07,.41s-.03,.28-.08,.41m1.25-1.43c-.27-.35-.63-.59-1.09-.73,.38-.17,.66-.42,.86-.76,.2-.34,.29-.71,.29-1.1,0-.25-.04-.49-.13-.74-.09-.24-.21-.46-.37-.65-.16-.19-.35-.35-.58-.47-.23-.12-.48-.18-.76-.18h-4.29V12.23h3.87c.35,0,.69-.05,1-.14,.31-.09,.59-.22,.83-.4s.42-.39,.56-.64c.14-.26,.2-.55,.2-.89,0-.53-.13-.97-.4-1.32m-12.04,1.84c-.25-.15-.45-.34-.6-.58-.15-.24-.26-.52-.33-.83-.06-.31-.1-.64-.1-.98V4.21h-1.56v4.09c0,.52,.06,1.02,.19,1.5,.12,.48,.33,.9,.6,1.27,.28,.36,.64,.66,1.09,.88,.45,.22,.99,.33,1.63,.33s1.15-.11,1.59-.32c.44-.21,.81-.5,1.09-.86,.28-.36,.49-.78,.62-1.26,.13-.48,.2-1,.2-1.55V4.21h-1.55v4.09c0,.33-.03,.65-.1,.96-.06,.31-.17,.59-.33,.83-.15,.24-.36,.44-.61,.59-.25,.15-.56,.23-.93,.23s-.67-.07-.92-.22m-3.14,1.54v-1.37h-4.09V4.21h-1.56V12.23h5.65Zm-13.41-2.56c.18,.49,.44,.93,.78,1.32,.33,.39,.73,.7,1.2,.94s.97,.36,1.53,.36c.32,0,.64-.04,.96-.12,.32-.08,.63-.2,.93-.36s.55-.35,.78-.58c.23-.23,.4-.49,.53-.8l-1.28-.73c-.09,.22-.21,.4-.35,.56-.14,.15-.3,.28-.47,.38-.17,.1-.34,.17-.53,.21-.18,.04-.36,.07-.53,.07-.33,0-.64-.08-.91-.23-.28-.15-.52-.35-.72-.6-.2-.25-.35-.54-.46-.86-.11-.33-.16-.67-.16-1.02,0-.32,.05-.63,.14-.96,.09-.32,.23-.6,.42-.86,.18-.25,.42-.46,.69-.61,.28-.16,.6-.24,.97-.24,.17,0,.34,.02,.52,.06,.18,.04,.36,.11,.52,.2,.17,.09,.33,.21,.47,.37,.14,.15,.26,.35,.35,.57l1.2-.82c-.25-.52-.63-.94-1.16-1.26-.52-.32-1.14-.49-1.86-.49-.61,0-1.15,.12-1.63,.35-.47,.23-.88,.54-1.2,.92-.33,.38-.58,.8-.75,1.28-.17,.48-.26,.96-.26,1.45,0,.51,.09,1.02,.28,1.51" fill="#005095"/>
    </svg>
  );
};
